import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { fmtTL, fmtInt, fmtDate } from "@/lib/format";
import { StatusBadge, Badge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Eye, Edit, ArrowDownToLine, ArrowUpFromLine, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";

export default function Products() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [brand, setBrand] = useState("all");
  const [src, setSrc] = useState("all");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (cat !== "all") params.category_id = cat;
      if (brand !== "all") params.brand_id = brand;
      if (src !== "all") params.source_type = src;
      if (status !== "all") params.stock_status = status;
      const { data } = await api.get("/products", { params });
      setItems(data);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/definitions/categories").then((r) => setCategories(r.data));
    api.get("/definitions/brands").then((r) => setBrands(r.data));
    load();
  }, []);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [q, cat, brand, src, status]);

  const catMap = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c.name])), [categories]);
  const brandMap = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b.name])), [brands]);

  const stockStatus = (p) => {
    const cs = Number(p.current_stock || 0);
    const crit = Number(p.critical_stock_level || 0);
    if (cs <= 0) return "out";
    if (cs <= crit) return "critical";
    return "ok";
  };

  const totalPages = Math.ceil(items.length / pageSize);
  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  const deactivate = async (id) => {
    if (!window.confirm("Bu ürünü kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Ürün silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
  };

  const canEdit = hasRole(user, "admin", "warehouse");
  const canSeePrice = user?.role !== "sales";

  return (
    <div className="space-y-4">
      <div className="azd-card p-4 lg:p-5">
        <div className="grid lg:grid-cols-12 gap-3 items-end">
          <div className="lg:col-span-4">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Ara</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <Input
                data-testid="products-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ürün adı veya kodu..."
                className="pl-9 h-10"
              />
            </div>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Kategori</label>
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="filter-category" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Marka</label>
            <Select value={brand} onValueChange={setBrand}>
              <SelectTrigger data-testid="filter-brand" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Kaynak</label>
            <Select value={src} onValueChange={setSrc}>
              <SelectTrigger data-testid="filter-source" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="production">Üretim</SelectItem>
                <SelectItem value="ready">Hazır Alım</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-[#111827] mb-1.5">Stok</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filter-stock-status" className="h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="ok">Yeterli</SelectItem>
                <SelectItem value="critical">Kritik</SelectItem>
                <SelectItem value="out">Stok Yok</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-[#6B7280] font-medium">
          Toplam <span className="text-[#08285A] font-bold">{items.length}</span> ürün
        </div>
        <div className="flex gap-2">
          <ExportButton type="products" />
          {canEdit && (
            <Button
              data-testid="new-product-btn"
              onClick={() => navigate("/products/new")}
              className="bg-[#0B3676] hover:bg-[#08285A] text-white"
            >
              <Plus size={16} className="mr-1" /> Yeni Ürün
            </Button>
          )}
        </div>
      </div>

      <div className="azd-card overflow-hidden">
        <div className="overflow-x-auto azd-scrollbar">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Kod</th>
                <th className="text-left px-4 py-3 font-semibold">Ürün Adı</th>
                <th className="text-left px-4 py-3 font-semibold">Kategori</th>
                <th className="text-left px-4 py-3 font-semibold">Marka</th>
                <th className="text-left px-4 py-3 font-semibold">Kaynak</th>
                <th className="text-right px-4 py-3 font-semibold">Stok</th>
                <th className="text-right px-4 py-3 font-semibold">Kritik</th>
                <th className="text-center px-4 py-3 font-semibold">Durum</th>
                {canSeePrice && (
                  <th className="text-right px-4 py-3 font-semibold">Satış Fiyatı</th>
                )}
                <th className="text-right px-4 py-3 font-semibold">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-8 text-center text-[#6B7280]">Yükleniyor...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={10} className="p-8 text-center text-[#6B7280]">Ürün bulunamadı</td></tr>
              ) : (
                paged.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`product-row-${p.id}`}
                    className="border-t border-[#E5E7EB] hover:bg-[#F6F8FB]/50"
                  >
                    <td className="px-4 py-3 font-mono text-xs text-[#0B3676]">{p.product_code}</td>
                    <td className="px-4 py-3 font-semibold text-[#111827]">{p.product_name}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{catMap[p.category_id] || "-"}</td>
                    <td className="px-4 py-3 text-[#6B7280] text-xs">{brandMap[p.brand_id] || "-"}</td>
                    <td className="px-4 py-3 text-xs">
                      <Badge variant={p.source_type === "production" ? "info" : "default"}>
                        {p.source_type === "production" ? "Üretim" : "Hazır"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-[#08285A]">
                      {fmtInt(p.current_stock)} <span className="text-[#6B7280] font-normal text-xs">{p.unit}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-[#6B7280]">
                      {fmtInt(p.critical_stock_level)} {p.unit}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={stockStatus(p)} />
                    </td>
                    {canSeePrice && (
                      <td className="px-4 py-3 text-right font-semibold text-[#0B3676]">
                        {fmtTL(p.calculated_sales_price)}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => navigate(`/products/${p.id}`)}
                        data-testid={`view-product-${p.id}`}
                        className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded"
                        title="Görüntüle"
                      ><Eye size={14} /></button>
                      {canEdit && (
                        <>
                          <button
                            onClick={() => navigate(`/products/${p.id}/edit`)}
                            data-testid={`edit-product-${p.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#0B3676] hover:bg-[#0B3676]/10 rounded ml-1"
                            title="Düzenle"
                          ><Edit size={14} /></button>
                          <button
                            onClick={() => deactivate(p.id)}
                            data-testid={`delete-product-${p.id}`}
                            className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded ml-1"
                            title="Sil"
                          ><Trash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-[#E5E7EB] flex items-center justify-between text-sm">
            <div className="text-[#6B7280]">
              Sayfa {page} / {totalPages}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1}
                      onClick={() => setPage(page - 1)} data-testid="prev-page">
                Önceki
              </Button>
              <Button variant="outline" size="sm" disabled={page === totalPages}
                      onClick={() => setPage(page + 1)} data-testid="next-page">
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
